import AttachFile from "@mui/icons-material/AttachFile";
import CheckCircle from "@mui/icons-material/CheckCircle";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { Alert, alpha, Box, Button, Link, Stack, SvgIconProps, Typography } from "@mui/material";
import { SubmissionStatus } from "generated-graphql/graphql";
import { ComponentType, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { SectionHeader } from "./SectionHeader";
import {
  buildComplianceTimeline,
  type ComplianceLine,
  type ComplianceLinesInput,
  daysBetweenUtc,
  getComplianceLines,
  isSubjectToEManifestUpload,
  NOT_SUBJECT_LABEL,
} from "./summaryStatus";
import { TimelineFlow } from "./TimelineSection";
import { ManifestDetail } from "./types";

/** Official eCFR text of the regulation the upload obligations come from. */
const ECFR_262_42_URL = "https://www.ecfr.gov/current/title-40/section-262.42";

type ComplianceManifest = Pick<
  ManifestDetail,
  | "isEManifest"
  | "daysSinceShipment"
  | "eManifestComplianceEscalation"
  | "isUniformHazardousManifest"
  | "reportedWasteGeneratorStatus"
  | "exceptionReportSubmission"
  | "transporterContactedAt"
  | "shippedDate"
  | "eManifestCertifiedDate"
>;

interface ComplianceSectionProps {
  manifest: ManifestDetail;
  tenantId: string;
  showExceptionReporting: boolean;
  hasProofOfContact: boolean;
  creatingExceptionReport: boolean;
  onAddProofOfContact: () => void;
  onCreateExceptionReport: () => void;
  /** Preview the uploaded proof-of-contact document, when one exists. */
  onViewProofOfContact?: () => void;
  /**
   * Read-only mode (customer view): render the same compliance status and
   * timeline, but hide the staff remediation actions (Add Proof of Contact,
   * Create Exception Report). The obligation text, status lines, and links to a
   * started/filed exception report stay, so customers see the same compliance
   * story they already see in the manifest grid's e-Manifest column.
   */
  readOnly?: boolean;
}

function buildLinesInput(
  manifest: ComplianceManifest,
  hasProofOfContact: boolean
): ComplianceLinesInput {
  let exceptionReportStatus: "none" | "started" | "filed" = "none";
  if (manifest.exceptionReportSubmission?.status === SubmissionStatus.FilingComplete) {
    exceptionReportStatus = "filed";
  } else if (manifest.exceptionReportSubmission) {
    exceptionReportStatus = "started";
  }
  return {
    subjectToUpload: isSubjectToEManifestUpload(manifest.reportedWasteGeneratorStatus),
    notSubjectLabel: NOT_SUBJECT_LABEL[manifest.reportedWasteGeneratorStatus ?? ""],
    escalation: manifest.eManifestComplianceEscalation,
    isUniformHazardousManifest: manifest.isUniformHazardousManifest,
    hasEManifest: !!manifest.isEManifest,
    daysSinceShipment: manifest.daysSinceShipment ?? 0,
    contacted: manifest.transporterContactedAt != null || hasProofOfContact,
    exceptionReportStatus,
    daysToCertification: daysBetweenUtc(manifest.shippedDate, manifest.eManifestCertifiedDate),
    eManifestCertifiedDate: manifest.eManifestCertifiedDate,
  };
}

const EXCEPTION_KINDS = new Set<ComplianceLine["kind"]>([
  "exceptionRequired",
  "exceptionStarted",
  "exceptionFiled",
]);

/** The lines the section renders. The section is always shown, so this includes
 * the positive on-track / uploaded / not-subject states too; only exception
 * lines are gated, by their feature flag. */
function visibleComplianceLines(args: {
  manifest: ComplianceManifest;
  hasProofOfContact: boolean;
  showExceptionReporting: boolean;
}): ComplianceLine[] {
  return getComplianceLines(buildLinesInput(args.manifest, args.hasProofOfContact)).filter((l) => {
    if (!args.showExceptionReporting && EXCEPTION_KINDS.has(l.kind)) return false;
    return true;
  });
}

/**
 * An action-required callout: a tinted Alert that sets the obligation apart
 * from the passive status lines. The lead phrase is bold, the day-count detail
 * stays regular weight, and the supporting text/action sit inside the tint so
 * the whole obligation reads as one block.
 */
function ActionCallout({
  severity,
  title,
  detail,
  children,
}: {
  severity: "success" | "warning" | "error";
  title: string;
  detail?: string;
  children?: ReactNode;
}) {
  return (
    <Alert
      severity={severity}
      sx={{
        "& .MuiAlert-message": { minWidth: 0 },
        // The section card's gray surface washes out the alert tint; a faint
        // same-hue outline keeps the callout's edge legible.
        border: 1,
        borderColor: (theme) => alpha(theme.palette[severity].main, 0.25),
      }}
    >
      <Typography variant="body2">
        <Box component="span" sx={{ fontWeight: 600 }}>
          {title}
        </Box>
        {detail ? ` — ${detail}` : null}
      </Typography>
      {children}
    </Alert>
  );
}

/**
 * One compliance line: an icon that sets the line off (like a bullet), a bold
 * title in the normal text color, and optional supporting text / actions. No
 * box — the lines sit directly in the section card.
 */
function ComplianceItem({
  Icon,
  color,
  title,
  bold = true,
  children,
}: {
  Icon: ComponentType<SvgIconProps>;
  color: string;
  title: string;
  /** Bold by default; positive/reassurance lines render at normal weight. */
  bold?: boolean;
  children?: ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Icon sx={{ fontSize: "1.125rem", color, flexShrink: 0, mt: "1px" }} />
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={bold ? 600 : 400}>
          {title}
        </Typography>
        {children}
      </Stack>
    </Stack>
  );
}

/**
 * e-Manifest upload compliance: the 45-/60-day obligations, exception-report
 * state, and a transporter/TSDF-contacted note — each as an icon-led line,
 * driven by the same {@link getComplianceLines} model the Summary uses. Only
 * rendered when {@link hasComplianceContent} is true. Source-agnostic.
 */
export function ComplianceSection({
  manifest,
  tenantId,
  showExceptionReporting,
  hasProofOfContact,
  creatingExceptionReport,
  onAddProofOfContact,
  onCreateExceptionReport,
  onViewProofOfContact,
  readOnly = false,
}: ComplianceSectionProps) {
  const lines = visibleComplianceLines({ manifest, hasProofOfContact, showExceptionReporting });

  const submission = manifest.exceptionReportSubmission;
  const exceptionFiled = submission?.status === SubmissionStatus.FilingComplete;

  // The 45/60-day obligations as a mini-timeline — present only once the
  // 45-day window was crossed without an e-Manifest (see buildComplianceTimeline).
  const timelineEvents = showExceptionReporting
    ? buildComplianceTimeline({
        subjectToUpload: isSubjectToEManifestUpload(manifest.reportedWasteGeneratorStatus),
        isUniformHazardousManifest: manifest.isUniformHazardousManifest,
        shippedDate: manifest.shippedDate,
        escalation: manifest.eManifestComplianceEscalation,
        eManifestCertifiedDate: manifest.eManifestCertifiedDate,
        contacted: manifest.transporterContactedAt != null || hasProofOfContact,
        contactedAt: manifest.transporterContactedAt,
        exceptionReportFiled: exceptionFiled,
        // A submission exists but isn't filed yet → "In progress".
        exceptionReportStarted: submission != null && !exceptionFiled,
        exceptionFiledDate: submission?.filedAt ?? null,
      })
    : [];

  const renderLine = (line: ComplianceLine): ReactNode => {
    switch (line.kind) {
      case "contactNeeded":
        return (
          <ActionCallout
            key={line.kind}
            severity="warning"
            title={line.title ?? line.message}
            detail={line.detail}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {/* color=inherit: keeps the citation clickable without a blue
                  accent competing with the action button below. */}
              <Link href={ECFR_262_42_URL} target="_blank" rel="noopener" color="inherit">
                40 CFR 262.42(a)
              </Link>
              : if you haven&apos;t received a signed manifest within 45 days, contact the
              transporter/TSDF.
            </Typography>
            {!readOnly && (
              <Button
                size="small"
                startIcon={<AttachFile />}
                onClick={onAddProofOfContact}
                sx={{ mt: 1, ml: -0.5 }}
              >
                Add Proof of Contact
              </Button>
            )}
          </ActionCallout>
        );
      case "exceptionRequired":
        return (
          <ActionCallout
            key={line.kind}
            severity="error"
            title={line.title ?? line.message}
            detail={line.detail}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              <Link href={ECFR_262_42_URL} target="_blank" rel="noopener" color="inherit">
                40 CFR 262.42(a)
              </Link>
              : After contacting the transporter/TSDF, if you still haven&apos;t received a signed
              manifest after 60 days, file an Exception Report with the EPA.
            </Typography>
            {!readOnly && (
              <Button
                size="small"
                startIcon={<DescriptionOutlined />}
                disabled={creatingExceptionReport}
                onClick={onCreateExceptionReport}
                sx={{ mt: 1, ml: -0.5 }}
              >
                {creatingExceptionReport ? "Creating…" : "Create Exception Report"}
              </Button>
            )}
          </ActionCallout>
        );
      case "exceptionFiled":
        return (
          <ActionCallout key={line.kind} severity="success" title="Exception report filed">
            {submission && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <Link
                  component={RouterLink}
                  to={`/o/${tenantId}/waste/submissions/${submission.id}`}
                >
                  {submission.title}
                </Link>
              </Typography>
            )}
          </ActionCallout>
        );
      case "exceptionStarted":
        return (
          <ComplianceItem
            key={line.kind}
            Icon={InfoIcon}
            color="info.main"
            title="Exception report started"
          >
            {submission && (
              <Typography variant="body2">
                <Link
                  component={RouterLink}
                  to={`/o/${tenantId}/waste/submissions/${submission.id}`}
                >
                  {submission.title}
                </Link>
              </Typography>
            )}
          </ComplianceItem>
        );
      case "contacted":
        return (
          <ActionCallout key={line.kind} severity="success" title="Transporter/TSDF contacted">
            {onViewProofOfContact && (
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onViewProofOfContact}
                sx={{ display: "block", mt: 0.5 }}
              >
                Proof of Contact
              </Link>
            )}
          </ActionCallout>
        );
      case "onTrack":
      case "uploaded":
      case "notSubject":
        // Positive/neutral states — shown now that the section is always
        // visible. Normal weight: reassurance, not a call to action. The icon
        // follows the line's level: "uploaded" is only a green check when the
        // upload wasn't delinquent (a 60+-day-late certification comes through
        // at level info and must not read as all-clear).
        return (
          <ComplianceItem
            key={line.kind}
            Icon={line.level === "ok" ? CheckCircle : InfoIcon}
            color={line.level === "ok" ? "success.main" : "info.main"}
            title={line.message}
            bold={false}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <SectionHeader title="e-Manifest Upload Compliance" />
      {timelineEvents.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <TimelineFlow events={timelineEvents} />
        </Box>
      )}
      <Stack spacing={1.5}>{lines.map(renderLine)}</Stack>
    </Box>
  );
}
